import {
  Injectable,
  ErrorHandler
} from '@angular/core';
import {
  Subject,
  Observable,
  Notification,
  BehaviorSubject,
  of
} from 'rxjs';
import { Action } from '@ngrx/store';
import {
  dematerialize,
  filter,
  map,
  exhaustMap,
  mergeMap,
  groupBy,
  delay,
  first,
  tap,
  finalize
} from 'rxjs/operators';
import { mergeFormEffects } from './forms-resolver';
import { FormNotification } from './form-notification';
import { NGXLogger } from 'ngx-logger';
import { AbstractFormDefinition } from './abstract-form-definition';
import { LifeCycleService } from '../life-cycle/life-cycle.service';

@Injectable({ providedIn: 'root' })
export class FormsSources extends Subject<any> {

  public formDefinitions: Array<AbstractFormDefinition<any, any>> = [];

  public registerFormDefinitions = new Subject<AbstractFormDefinition<any, any>>();

  public registerFormDefinitionIds: string[] = [];

  public beforeReady: Array<AbstractFormDefinition<any, any>> = [];

  public beforeReadyCompleted$ = new BehaviorSubject(false);

  addForm(formSourceInstance: AbstractFormDefinition<any, any>) {
    this.logger.trace(`[FormsSources::addForm::${formSourceInstance.formId}] call`, formSourceInstance);
    if (this.registerFormDefinitionIds.some(formId => formId === formSourceInstance.formId)) {
      this.logger.info(`[FormsSources::addForm::${formSourceInstance.formId}] form definition is already registered`);
    } else {
      this.registerFormDefinitionIds.push(formSourceInstance.formId);
      if (this.lifecycle.appIsReady) {
        this.registerFormDefinitions.next(formSourceInstance);
      } else {
        this.beforeReady.push(formSourceInstance);
      }
    }
  }

  constructor(
    private errorHandler: ErrorHandler,
    private logger: NGXLogger,
    private lifecycle: LifeCycleService,
  ) {
    super();
    this.registerFormDefinitions.subscribe(formDefinition => {
      formDefinition.init();
      this.formDefinitions.push(formDefinition);
      this.next(formDefinition);
    });
    this.lifecycle.appIsReady$.pipe(
      delay(1),
      mergeMap(() => this.beforeReady),
      tap(formDefinition => {
        formDefinition.init();
        this.formDefinitions.push(formDefinition);
        this.next(formDefinition);
      }),
      finalize(() => this.beforeReadyCompleted$.next(true))
    ).subscribe();
  }

  public getForm<FD extends AbstractFormDefinition<any, any>>(
    formId: string
  ): FD | null {
    return (
      (this.formDefinitions.find(fd => fd.formId === formId) as any) || null
    );
  }

  public getForm$<FD extends AbstractFormDefinition<any, any>>(
    formId: string
  ): Observable<FD | null> {
    if (this.beforeReadyCompleted$.value) {
      return of(this.getForm(formId))
    } else {
      return this.beforeReadyCompleted$.pipe(
        filter(Boolean),
        first(),
        map(() => this.getForm(formId))
      );
    }
  }

  /**
   * @internal
   */
  toActions(): Observable<Action> {
    this.logger.trace('[FormsSources::toActions] call');
    return this.pipe(
      groupBy(instance => Object.getPrototypeOf(instance)),
      mergeMap(source$ =>
        source$.pipe(
          tap(action => console.warn('before toActions', action)),
          exhaustMap(sourceInstance =>
            mergeFormEffects(sourceInstance, this.errorHandler)
          ),
          tap(action => console.warn('after toActions', action)),
          map(output => {
            reportInvalidActions(output, this.errorHandler);
            return output.notification;
          }),
          filter(
            (notification): notification is Notification<Action> =>
              notification.kind === 'N'
          ),
          dematerialize()
        )
      )
    );
  }
}

export function reportInvalidActions(
  output: FormNotification,
  reporter: ErrorHandler
) {
  if (output.notification.kind === 'N') {
    const action = output.notification.value;
    const isInvalidAction = !isAction(action);

    if (isInvalidAction) {
      reporter.handleError(
        new Error(
          `Effect ${getEffectName(
            output
          )} dispatched an invalid action: ${stringify(action)}`
        )
      );
    }
  }
}

function isAction(action: any): action is Action {
  return (
    typeof action !== 'function' &&
    action &&
    action.type &&
    typeof action.type === 'string'
  );
}

function getEffectName({
  propertyName,
  sourceInstance,
  sourceName
}: FormNotification) {
  const isMethod = typeof sourceInstance[propertyName] === 'function';

  return `"${sourceName}.${propertyName}${isMethod ? '()' : ''}"`;
}

function stringify(action: Action | null | undefined) {
  try {
    return JSON.stringify(action);
  } catch {
    return action;
  }
}
